/**
 * Frontend Business API Initialization (DRY-01.1)
 *
 * Initializes the unified shared/api/businessApi module with the frontend's
 * Axios HTTP client. This module delegates all CRUD operations to the shared layer.
 *
 * DRY-01.1 Phase: Consolidates all business entity API patterns into a single,
 * tenant-safe, fully-typed module accessed via businessApi namespace.
 */

import { apiClient as businessApiClient } from './apiService';
import {
  businessApi as sharedBusinessApi,
  setBusinessHttpClient,
  type IBusinessHttpClient,
} from '../../../shared/api';

// ─── Axios → IBusinessHttpClient adapter ──────────────────────────────────
// businessApiClient is an AxiosInstance; this slim wrapper satisfies the shared
// IBusinessHttpClient interface so businessApi can drive it.
const businessHttpClient: IBusinessHttpClient = {
  get: <T>(path: string, params?: Record<string, unknown>) =>
    businessApiClient.get<T>(path, { params }).then((r) => r.data),
  post: <T>(path: string, data?: unknown) =>
    businessApiClient.post<T>(path, data).then((r) => r.data),
  patch: <T>(path: string, data?: unknown) =>
    businessApiClient.patch<T>(path, data).then((r) => r.data),
  put: <T>(path: string, data?: unknown) =>
    businessApiClient.put<T>(path, data).then((r) => r.data),
  delete: (path: string) => businessApiClient.delete(path).then(() => undefined),
};

// ─── Initialize Shared Business API ───────────────────────────────────────
// Call this during app startup (before any component renders)
setBusinessHttpClient(businessHttpClient);

// ─── Re-export Shared Module ──────────────────────────────────────────────
// Components should import from @/shared/api/businessApi directly,
// but we also export here for backward compatibility during migration.

export { businessApi as sharedBusinessApi } from '../../../shared/api';
export { verifyCarrierFmcsa, getCarrierLanes } from '../../../shared/api';
export type { CarrierLaneSummary, CarrierLanesResponse } from '../../../shared/api';
export type {
  Customer,
  Supplier,
  Contact,
  PurchaseOrder,
  AccountsReceivable,
  Plant,
  Carrier,
  PaginatedResponse,
} from '../../../shared/api';
export type { CarrierFmcsaVerifyResponse } from '../../../shared/api';

// Legacy named entity exports expected by existing hooks/components.
// Keep these aliases until all consumers migrate to sharedBusinessApi.<entity>.
export const customersApi = sharedBusinessApi.customers;
export const suppliersApi = sharedBusinessApi.suppliers;
export const contactsApi = sharedBusinessApi.contacts;
export const purchaseOrdersApi = sharedBusinessApi.purchaseOrders;
export const accountsReceivableApi = sharedBusinessApi.accountsReceivable;
export const plantsApi = sharedBusinessApi.plants;
export const carriersApi = sharedBusinessApi.carriers;

// Legacy exports for backward compatibility (during migration phase)
export const businessApi = businessApiClient;
export default businessApiClient;
